import { ClinicalInput, Diagnosis, EngineAlert, EngineResult, ProvisionalPatternKind, evaluateClinicalState } from './engine'

export type EpisodeStatus = 'active' | 'resolution_candidate' | 'resolved' | 'closed'
export type DueState = 'due' | 'soon' | 'ok' | 'unknown'
export type FeedingStatus = 'unknown' | 'fasting' | 'oral' | 'enteral' | 'parenteral'
export type TransitionPlanStatus = 'draft' | 'documented' | 'alternative'
export type ResolutionConfirmationMode = 'consensus_bohb' | 'manual_no_bohb'
export type FluidType = 'balanced_crystalloid' | 'saline_09' | 'saline_045' | 'dextrose_5' | 'dextrose_10' | 'other'

export interface EpisodeTreatmentSnapshot {
  insulinUnitsHour?: number
  fluidMlHour?: number
  fluidType?: FluidType
  potassiumMmolPerL?: number
  potassiumMmolHour?: number
}

export interface EpisodeObservation {
  id: string
  collectedAt: string
  input: ClinicalInput
  result: EngineResult
  treatment?: EpisodeTreatmentSnapshot
}

export interface TreatmentEvent extends EpisodeTreatmentSnapshot {
  id: string
  occurredAt: string
  note?: string
}

export interface TransitionPlan {
  status: TransitionPlanStatus
  selectedTddUnits?: number
  basalUnits?: number
  basalGivenAt?: string
  ivStopPlannedAt?: string
  feedingStatus?: FeedingStatus
  prandialPlan?: string
  correctionPlan?: string
  hypoglycemiaRisk?: boolean
  homeTddUnits?: number
  ivWindowAcceptedAsStable?: boolean
  alternativeReason?: string
  documentedAt?: string
}

export interface ResolutionConfirmation {
  mode: ResolutionConfirmationMode
  confirmedAt: string
  reason?: string
}

export interface Episode {
  id: string
  startedAt: string
  status: EpisodeStatus
  diagnosis: Diagnosis
  observations: EpisodeObservation[]
  treatmentEvents?: TreatmentEvent[]
  transitionPlan?: TransitionPlan
  resolutionConfirmation?: ResolutionConfirmation
}

export interface EpisodeRates {
  hours?: number
  glucoseIntervalHours?: number
  sodiumIntervalHours?: number
  effectiveOsmolalityIntervalHours?: number
  totalOsmolalityIntervalHours?: number
  betaHydroxybutyrateIntervalHours?: number
  glucoseMgDlHour?: number
  sodiumMmolLHour?: number
  effectiveOsmolalityHour?: number
  totalOsmolalityHour?: number
  betaHydroxybutyrateHour?: number
}

export interface MonitoringDue {
  glucose: DueState
  metabolicPanel: DueState
  ketones: DueState
  glucoseAgeHours?: number
  metabolicAgeHours?: number
  ketoneAgeHours?: number
}

export interface EpisodeEvaluation {
  latest: EpisodeObservation
  previous?: EpisodeObservation
  rates: EpisodeRates
  longitudinalAlerts: EngineAlert[]
  monitoring: MonitoringDue
  status: EpisodeStatus
}

export interface IvInsulinWindow {
  requestedHours: number
  coveredHours: number
  averageUnitsHour?: number
  projectedTddUnits?: number
  complete: boolean
  changesInWindow: number
  startAt: string
  endAt: string
}

export interface DocumentedFluidExposure {
  coveredHours: number
  intervalHours: number
  volumeMl: number
  completeFromEpisodeStart: boolean
  currentRateMlHour?: number
  startAt: string
  endAt: string
}

export interface ManualDkaResolutionEligibility {
  eligible: boolean
  betaHydroxybutyrateUnavailable: boolean
  acidBaseRecovered: boolean
  glucoseSupportive: boolean
  hyperosmolarComponentResolved: boolean
}

const finite = (value: number | undefined): value is number => typeof value === 'number' && Number.isFinite(value)
const round = (value: number, digits = 1) => { const p = 10 ** digits; return Math.round(value * p) / p }
const hoursBetween = (from: string, to: string) => {
  const ms = new Date(to).getTime() - new Date(from).getTime()
  if (!Number.isFinite(ms) || ms < 0) return undefined
  return ms / 3_600_000
}
const rate = (previous: number | undefined, latest: number | undefined, hours: number | undefined) => {
  if (!finite(previous) || !finite(latest) || !finite(hours) || hours <= 0) return undefined
  return round((latest - previous) / hours, 1)
}
const roundedHours = (hours: number | undefined) => finite(hours) ? round(hours, 2) : undefined
const hasTreatmentData = (treatment: EpisodeTreatmentSnapshot | undefined) => Boolean(treatment && (
  treatment.insulinUnitsHour !== undefined || treatment.fluidMlHour !== undefined || treatment.fluidType !== undefined ||
  treatment.potassiumMmolPerL !== undefined || treatment.potassiumMmolHour !== undefined
))

function isHyperosmolarDiagnosis(diagnosis: Diagnosis) { return diagnosis === 'HHS' || diagnosis === 'MIXED_DKA_HHS' }
function isDkaEpisode(diagnosis: Diagnosis) { return diagnosis === 'DKA' || diagnosis === 'EUGLYCEMIC_DKA' || diagnosis === 'MIXED_DKA_HHS' }
function isHyperosmolarProvisional(kind: ProvisionalPatternKind | undefined) {
  return kind === 'HHS_POSSIBLE_NO_KETONES' || kind === 'MIXED_POSSIBLE_NO_KETONES'
}

export function makeObservation(input: ClinicalInput, collectedAt = new Date().toISOString(), treatment?: EpisodeObservation['treatment']): EpisodeObservation {
  return { id: crypto.randomUUID(), collectedAt, input, result: evaluateClinicalState(input), treatment }
}

export function makeTreatmentEvent(occurredAt: string, treatment: Omit<TreatmentEvent, 'id' | 'occurredAt'>): TreatmentEvent {
  return { id: crypto.randomUUID(), occurredAt, ...treatment }
}

export function createEpisode(initial: EpisodeObservation): Episode {
  const treatmentEvents: TreatmentEvent[] = []
  if (hasTreatmentData(initial.treatment)) {
    treatmentEvents.push(makeTreatmentEvent(initial.collectedAt, initial.treatment!))
  }
  return {
    id: crypto.randomUUID(),
    startedAt: initial.collectedAt,
    status: 'active',
    diagnosis: initial.result.diagnosis,
    observations: [initial],
    treatmentEvents
  }
}

export function appendTreatmentEvent(episode: Episode, event: TreatmentEvent): Episode {
  const treatmentEvents = [...(episode.treatmentEvents ?? []), event].sort(
    (a, b) => new Date(a.occurredAt).getTime() - new Date(b.occurredAt).getTime()
  )
  return { ...episode, treatmentEvents }
}

export function latestTreatmentEvent(episode: Episode, at = new Date().toISOString()): TreatmentEvent | undefined {
  const target = new Date(at).getTime()
  return [...(episode.treatmentEvents ?? [])]
    .filter((event) => new Date(event.occurredAt).getTime() <= target)
    .sort((a, b) => new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime())[0]
}

export function latestTreatmentEventWith<K extends keyof EpisodeTreatmentSnapshot>(episode: Episode, field: K, at = new Date().toISOString()): TreatmentEvent | undefined {
  const target = new Date(at).getTime()
  return [...(episode.treatmentEvents ?? [])]
    .filter((event) => event[field] !== undefined && new Date(event.occurredAt).getTime() <= target)
    .sort((a, b) => new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime())[0]
}

export function calculateIvInsulinWindow(episode: Episode, endAt?: string, requestedHours = 6): IvInsulinWindow {
  const endIso = endAt ?? episode.observations[episode.observations.length - 1]?.collectedAt ?? new Date().toISOString()
  const endMs = new Date(endIso).getTime()
  const startMs = endMs - requestedHours * 3_600_000
  const startIso = new Date(startMs).toISOString()
  const insulinEvents = [...(episode.treatmentEvents ?? [])]
    .filter((event) => finite(event.insulinUnitsHour) && new Date(event.occurredAt).getTime() <= endMs)
    .sort((a, b) => new Date(a.occurredAt).getTime() - new Date(b.occurredAt).getTime())

  const prior = [...insulinEvents].reverse().find((event) => new Date(event.occurredAt).getTime() <= startMs)
  const inside = insulinEvents.filter((event) => {
    const time = new Date(event.occurredAt).getTime()
    return time > startMs && time <= endMs
  })

  let cursorMs = startMs
  let currentRate = prior?.insulinUnitsHour
  let coveredMs = 0
  let units = 0
  let changesInWindow = 0

  for (const event of inside) {
    const eventMs = new Date(event.occurredAt).getTime()
    if (finite(currentRate) && eventMs > cursorMs) {
      const durationMs = eventMs - cursorMs
      units += currentRate * durationMs / 3_600_000
      coveredMs += durationMs
    }
    if (finite(currentRate) && event.insulinUnitsHour !== currentRate) changesInWindow += 1
    cursorMs = eventMs
    currentRate = event.insulinUnitsHour
  }

  if (finite(currentRate) && endMs > cursorMs) {
    const durationMs = endMs - cursorMs
    units += currentRate * durationMs / 3_600_000
    coveredMs += durationMs
  }

  const coveredHours = coveredMs / 3_600_000
  const averageUnitsHour = coveredHours > 0 ? round(units / coveredHours, 2) : undefined
  const complete = coveredHours >= requestedHours - 0.01

  return {
    requestedHours,
    coveredHours: round(coveredHours, 2),
    averageUnitsHour,
    projectedTddUnits: finite(averageUnitsHour) ? round(averageUnitsHour * 24, 1) : undefined,
    complete,
    changesInWindow,
    startAt: startIso,
    endAt: endIso
  }
}

export function calculateDocumentedFluidExposure(episode: Episode, endAt = new Date().toISOString()): DocumentedFluidExposure {
  const startMs = new Date(episode.startedAt).getTime()
  const endMs = new Date(endAt).getTime()
  const intervalMs = Math.max(0, endMs - startMs)
  const fluidEvents = [...(episode.treatmentEvents ?? [])]
    .filter((event) => finite(event.fluidMlHour) && new Date(event.occurredAt).getTime() <= endMs)
    .sort((a, b) => new Date(a.occurredAt).getTime() - new Date(b.occurredAt).getTime())

  const prior = [...fluidEvents].reverse().find((event) => new Date(event.occurredAt).getTime() <= startMs)
  const inside = fluidEvents.filter((event) => {
    const time = new Date(event.occurredAt).getTime()
    return time > startMs && time <= endMs
  })

  let cursorMs = startMs
  let currentRate = prior?.fluidMlHour
  let coveredMs = 0
  let volumeMl = 0

  for (const event of inside) {
    const eventMs = new Date(event.occurredAt).getTime()
    if (finite(currentRate) && eventMs > cursorMs) {
      const durationMs = eventMs - cursorMs
      volumeMl += currentRate * durationMs / 3_600_000
      coveredMs += durationMs
    }
    cursorMs = eventMs
    currentRate = event.fluidMlHour
  }

  if (finite(currentRate) && endMs > cursorMs) {
    const durationMs = endMs - cursorMs
    volumeMl += currentRate * durationMs / 3_600_000
    coveredMs += durationMs
  }

  const currentFluid = latestTreatmentEventWith(episode, 'fluidMlHour', endAt)
  return {
    coveredHours: round(coveredMs / 3_600_000, 2),
    intervalHours: round(intervalMs / 3_600_000, 2),
    volumeMl: round(volumeMl, 0),
    completeFromEpisodeStart: intervalMs === 0 || coveredMs >= intervalMs - 60_000,
    currentRateMlHour: currentFluid?.fluidMlHour,
    startAt: episode.startedAt,
    endAt
  }
}

export function canCloseEpisode(episode: Episode): boolean {
  if (episode.status !== 'resolved') return false
  const plan = episode.transitionPlan
  if (!plan) return false
  if (plan.status === 'alternative') return Boolean(plan.alternativeReason?.trim())
  if (plan.status !== 'documented') return false
  if (!finite(plan.selectedTddUnits) || plan.selectedTddUnits <= 0) return false
  if (!finite(plan.basalUnits) || plan.basalUnits <= 0) return false
  if (!plan.basalGivenAt || !plan.ivStopPlannedAt) return false
  const overlap = hoursBetween(plan.basalGivenAt, plan.ivStopPlannedAt)
  if (!finite(overlap) || overlap < 2) return false
  if (!plan.feedingStatus || plan.feedingStatus === 'unknown') return false
  return Boolean(plan.prandialPlan?.trim() && plan.correctionPlan?.trim())
}

export function getManualDkaResolutionEligibility(episode: Episode): ManualDkaResolutionEligibility {
  const latest = episode.observations[episode.observations.length - 1]
  const betaHydroxybutyrateUnavailable = !finite(latest.input.betaHydroxybutyrateMmolL)
  const acidBaseRecovered = (finite(latest.input.ph) && latest.input.ph >= 7.3) ||
    (finite(latest.input.bicarbonateMmolL) && latest.input.bicarbonateMmolL >= 18)
  const glucoseSupportive = finite(latest.input.glucoseMgDl) && latest.input.glucoseMgDl < 200
  const hyperosmolarComponentResolved = episode.diagnosis !== 'MIXED_DKA_HHS' || latest.result.resolution.hhs === true
  return {
    eligible: episode.status === 'active' && isDkaEpisode(episode.diagnosis) && betaHydroxybutyrateUnavailable && acidBaseRecovered && glucoseSupportive && hyperosmolarComponentResolved,
    betaHydroxybutyrateUnavailable,
    acidBaseRecovered,
    glucoseSupportive,
    hyperosmolarComponentResolved
  }
}

function previousObservationWith<T>(episode: Episode, latest: EpisodeObservation, selector: (observation: EpisodeObservation) => T | undefined) {
  const latestTime = new Date(latest.collectedAt).getTime()
  return [...episode.observations]
    .filter((observation) => observation.id !== latest.id && new Date(observation.collectedAt).getTime() < latestTime && selector(observation) !== undefined)
    .sort((a, b) => new Date(b.collectedAt).getTime() - new Date(a.collectedAt).getTime())[0]
}

export function calculateEpisodeRates(episode: Episode, latest: EpisodeObservation): EpisodeRates {
  const glucosePrevious = previousObservationWith(episode, latest, (o) => o.input.glucoseMgDl)
  const sodiumPrevious = previousObservationWith(episode, latest, (o) => o.input.sodiumMmolL)
  const effectivePrevious = previousObservationWith(episode, latest, (o) => o.result.calculations.effectiveOsmolality)
  const totalPrevious = previousObservationWith(episode, latest, (o) => o.result.calculations.totalOsmolality)
  const ketonePrevious = previousObservationWith(episode, latest, (o) => o.input.betaHydroxybutyrateMmolL)

  const hoursFor = (previous?: EpisodeObservation) => previous ? hoursBetween(previous.collectedAt, latest.collectedAt) : undefined
  const glucoseHours = hoursFor(glucosePrevious)
  const sodiumHours = hoursFor(sodiumPrevious)
  const effectiveHours = hoursFor(effectivePrevious)
  const totalHours = hoursFor(totalPrevious)
  const ketoneHours = hoursFor(ketonePrevious)
  const referenceHours = glucoseHours ?? sodiumHours ?? effectiveHours ?? totalHours ?? ketoneHours

  return {
    hours: roundedHours(referenceHours),
    glucoseIntervalHours: roundedHours(glucoseHours),
    sodiumIntervalHours: roundedHours(sodiumHours),
    effectiveOsmolalityIntervalHours: roundedHours(effectiveHours),
    totalOsmolalityIntervalHours: roundedHours(totalHours),
    betaHydroxybutyrateIntervalHours: roundedHours(ketoneHours),
    glucoseMgDlHour: rate(glucosePrevious?.input.glucoseMgDl, latest.input.glucoseMgDl, glucoseHours),
    sodiumMmolLHour: rate(sodiumPrevious?.input.sodiumMmolL, latest.input.sodiumMmolL, sodiumHours),
    effectiveOsmolalityHour: rate(effectivePrevious?.result.calculations.effectiveOsmolality, latest.result.calculations.effectiveOsmolality, effectiveHours),
    totalOsmolalityHour: rate(totalPrevious?.result.calculations.totalOsmolality, latest.result.calculations.totalOsmolality, totalHours),
    betaHydroxybutyrateHour: rate(ketonePrevious?.input.betaHydroxybutyrateMmolL, latest.input.betaHydroxybutyrateMmolL, ketoneHours)
  }
}

const dueState = (ageHours: number | undefined, dueAt: number, soonAt: number): DueState => {
  if (!finite(ageHours)) return 'unknown'
  if (ageHours >= dueAt) return 'due'
  if (ageHours >= soonAt) return 'soon'
  return 'ok'
}
const latestMatching = (episode: Episode, predicate: (observation: EpisodeObservation) => boolean) => [...episode.observations].filter(predicate).sort((a, b) => new Date(b.collectedAt).getTime() - new Date(a.collectedAt).getTime())[0]

export function getMonitoringDue(episode: Episode, now = new Date().toISOString()): MonitoringDue {
  const lastGlucose = latestMatching(episode, (o) => finite(o.input.glucoseMgDl))
  const lastMetabolic = latestMatching(episode, (o) => finite(o.input.sodiumMmolL) && finite(o.input.potassiumMmolL) && finite(o.input.bicarbonateMmolL) && finite(o.input.ph))
  const lastKetones = latestMatching(episode, (o) => finite(o.input.betaHydroxybutyrateMmolL))
  const glucoseAge = lastGlucose ? hoursBetween(lastGlucose.collectedAt, now) : undefined
  const metabolicAge = lastMetabolic ? hoursBetween(lastMetabolic.collectedAt, now) : undefined
  const ketoneAge = lastKetones ? hoursBetween(lastKetones.collectedAt, now) : undefined
  return {
    glucose: dueState(glucoseAge, 2, 1.5), metabolicPanel: dueState(metabolicAge, 4, 3.5), ketones: dueState(ketoneAge, 4, 3.5),
    glucoseAgeHours: finite(glucoseAge) ? round(glucoseAge, 1) : undefined,
    metabolicAgeHours: finite(metabolicAge) ? round(metabolicAge, 1) : undefined,
    ketoneAgeHours: finite(ketoneAge) ? round(ketoneAge, 1) : undefined
  }
}

export function evaluateEpisode(episode: Episode, now = new Date().toISOString()): EpisodeEvaluation {
  const latest = episode.observations[episode.observations.length - 1]
  const previous = episode.observations.length > 1 ? episode.observations[episode.observations.length - 2] : undefined
  const rates = calculateEpisodeRates(episode, latest)
  const longitudinalAlerts: EngineAlert[] = []
  const currentDiagnosis = episode.diagnosis === 'UNCLASSIFIED' ? latest.result.diagnosis : episode.diagnosis
  const indexPattern = episode.observations[0]?.result.provisionalPattern?.kind
  const hyperosmolarContext = isHyperosmolarDiagnosis(currentDiagnosis) || isHyperosmolarProvisional(indexPattern)

  if (hyperosmolarContext) {
    if (finite(rates.glucoseMgDlHour) && rates.glucoseMgDlHour < -120) longitudinalAlerts.push({ id: 'HHS-GLUCOSE-FALL-FAST', level: 'critical', title: 'Riduzione glicemica troppo rapida', message: `La glicemia sta diminuendo di circa ${Math.abs(rates.glucoseMgDlHour)} mg/dL/h; nel percorso iperosmolare la riduzione non dovrebbe superare 90–120 mg/dL/h.` })
    const osmRate = finite(rates.totalOsmolalityHour) ? rates.totalOsmolalityHour : rates.effectiveOsmolalityHour
    if (finite(osmRate) && osmRate < -8) longitudinalAlerts.push({ id: 'HHS-OSM-FALL-FAST', level: 'critical', title: 'Osmolalità in riduzione troppo rapida', message: `Riduzione stimata ${Math.abs(osmRate)} mOsm/kg/h; nel percorso iperosmolare la caduta raccomandata è 3–8 mOsm/kg/h.` })
    else if (finite(osmRate) && osmRate < 0 && osmRate > -3) longitudinalAlerts.push({ id: 'HHS-OSM-FALL-SLOW', level: 'info', title: 'Riduzione dell’osmolalità lenta', message: `Riduzione stimata ${Math.abs(osmRate)} mOsm/kg/h. Interpretare insieme a bilancio idrico, stato emodinamico e trattamento in corso.` })
  }

  if (isDkaEpisode(currentDiagnosis) && latest.result.resolution.dka === false && finite(latest.input.glucoseMgDl) && latest.input.glucoseMgDl < 250 && !latest.result.alerts.some((alert) => alert.id === 'DKA-GLUCOSE-BELOW-250')) {
    longitudinalAlerts.push({ id: 'DKA-GLUCOSE-BELOW-250', level: 'warning', title: 'Glicemia <250 mg/dL ma DKA non risolta', message: 'L’episodio DKA è ancora attivo: mantenere l’azione insulinica con apporto glucidico secondo protocollo fino alla risoluzione.' })
  }

  const manualEligibility = getManualDkaResolutionEligibility(episode)
  if (isDkaEpisode(currentDiagnosis) && manualEligibility.betaHydroxybutyrateUnavailable && manualEligibility.acidBaseRecovered) {
    longitudinalAlerts.push({
      id: 'DKA-BOHB-UNAVAILABLE-RESOLUTION',
      level: 'info',
      title: 'β-OHB non disponibile: risoluzione non automatizzabile',
      message: 'La chetonuria e l’anion gap non sono criteri affidabili di risoluzione secondo il consensus. Se il β-OHB non è ottenibile, documentare una decisione clinica secondo protocollo locale.'
    })
  }

  let status: EpisodeStatus = episode.status
  if (status === 'active') {
    if (isDkaEpisode(currentDiagnosis) && latest.result.resolution.dka === true) status = 'resolution_candidate'
    else if (currentDiagnosis === 'HHS' && latest.result.resolution.hhs === true) status = 'resolution_candidate'
  }

  return { latest, previous, rates, longitudinalAlerts, monitoring: getMonitoringDue(episode, now), status }
}

export function appendObservation(episode: Episode, observation: EpisodeObservation): Episode {
  const observations = [...episode.observations, observation].sort((a, b) => new Date(a.collectedAt).getTime() - new Date(b.collectedAt).getTime())
  const latest = observations[observations.length - 1]
  const diagnosis = episode.diagnosis === 'UNCLASSIFIED' && latest.result.diagnosis !== 'UNCLASSIFIED' ? latest.result.diagnosis : episode.diagnosis
  let next: Episode = { ...episode, observations, diagnosis }
  if (hasTreatmentData(observation.treatment)) {
    const changed: Omit<TreatmentEvent, 'id' | 'occurredAt'> = {}
    const insulinPrevious = latestTreatmentEventWith(next, 'insulinUnitsHour', observation.collectedAt)?.insulinUnitsHour
    const fluidPrevious = latestTreatmentEventWith(next, 'fluidMlHour', observation.collectedAt)?.fluidMlHour
    if (observation.treatment?.insulinUnitsHour !== undefined && observation.treatment.insulinUnitsHour !== insulinPrevious) changed.insulinUnitsHour = observation.treatment.insulinUnitsHour
    if (observation.treatment?.fluidMlHour !== undefined && observation.treatment.fluidMlHour !== fluidPrevious) changed.fluidMlHour = observation.treatment.fluidMlHour
    if (observation.treatment?.fluidType !== undefined) changed.fluidType = observation.treatment.fluidType
    if (observation.treatment?.potassiumMmolPerL !== undefined) changed.potassiumMmolPerL = observation.treatment.potassiumMmolPerL
    if (observation.treatment?.potassiumMmolHour !== undefined) changed.potassiumMmolHour = observation.treatment.potassiumMmolHour
    if (hasTreatmentData(changed)) {
      next = appendTreatmentEvent(next, makeTreatmentEvent(observation.collectedAt, changed))
    }
  }
  const evaluation = evaluateEpisode(next)
  return { ...next, status: evaluation.status }
}
